import React, { forwardRef, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ContactItem } from 'src/services/contact';
import { ControlledInput } from '../../ui/Input/Input';
import styles from './ContactOverlayForm.module.scss';
import CountrySelect from '../../ui/CountrySelect/CountrySelect';

const addressSchema = z.object({
  city: z.string().min(1, 'City is required'),
  street: z.string().min(1, 'Street is required'),
  street2: z.string().nullable().optional(),
  zip_code: z.string().min(1, 'Zip code is required'),
  country_code: z.string().min(1, 'Country is required'),
});

export const createContactSchema = z.object({
  address: addressSchema,
  name: z.string().min(1, 'Name is required'),
  company_name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  tax_id: z.string(),
});

export const updateContactSchema = createContactSchema.omit({ tax_id: true }).and(z.object({ id: z.number() }));

export type CreateContactFormValues = z.infer<typeof createContactSchema>;
export type UpdateContactFormValues = z.infer<typeof updateContactSchema>;

type Props = {
  readOnly?: boolean;
  partner?: ContactItem;
} & (
  | {
      mode: 'create';
      onSubmit: (data: CreateContactFormValues) => void;
    }
  | {
      mode: 'update';
      onSubmit: (data: UpdateContactFormValues) => void;
    }
);
export const ContactOverlayForm = forwardRef<HTMLFormElement, Props>(({ readOnly, onSubmit, partner, mode }, ref) => {
  const { handleSubmit, register, control, reset } = useForm<CreateContactFormValues | UpdateContactFormValues>({
    resolver: zodResolver(mode === 'create' ? createContactSchema : updateContactSchema),
    values: (partner as unknown as CreateContactFormValues) || undefined,
  });
  const { t } = useTranslation();
  useEffect(() => {
    reset(partner as any);
  }, [readOnly]);

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit as any)} className="flex flex-col flex-1 gap-16">
      <div className={styles.info}>
        <span className="typo-text-14--bold text-faded">{t('general.address')}</span>
        <div className={classNames('grid grid-cols-2 gap-16 w-full')}>
          <ControlledInput
            {...register('name')}
            control={control}
            label={!readOnly ? t('general.name') : undefined}
            placeholder=""
            disabled={readOnly}
            className="col-span-2"
          />
          <ControlledInput
            control={control}
            {...register('company_name')}
            label={!readOnly ? t('general.company_name') : undefined}
            placeholder=""
            disabled={readOnly}
            className="col-span-2"
          />
          <ControlledInput
            control={control}
            {...register('address.street')}
            label={!readOnly ? t('general.street') : undefined}
            placeholder=""
            disabled={readOnly}
            className="col-span-2"
          />
          <ControlledInput
            control={control}
            {...register('address.street2')}
            label={!readOnly ? t('general.street2') : undefined}
            placeholder=""
            disabled={readOnly}
            className="col-span-2"
          />

          <ControlledInput
            control={control}
            {...register('address.zip_code')}
            label={!readOnly ? t('general.zip_code') : undefined}
            placeholder={t('registration.placeholders.zip_code')}
            disabled={readOnly}
            className="col-span-1"
          />
          <ControlledInput
            control={control}
            {...register('address.city')}
            label={!readOnly ? t('general.city') : undefined}
            placeholder=""
            disabled={readOnly}
            className="col-span-1"
          />
          {mode === 'create' && (
            <ControlledInput
              control={control}
              {...register('tax_id')}
              label={!readOnly ? t('general.tax_id') : undefined}
              placeholder=""
              disabled={readOnly}
              className="col-span-1"
            />
          )}
          <CountrySelect
            name="address.country_code"
            className="col-span-2"
            isControlled
            control={control}
            parent={partner}
            label={!readOnly ? t('general.country') : undefined}
            editable={!readOnly}
          />
        </div>
      </div>
      <div className={classNames(styles.info, 'items-center')}>
        <span className="typo-text-14--bold text-faded">{t('general.email')}</span>
        <ControlledInput
          control={control}
          {...register('email')}
          placeholder={t('registration.placeholders.email')}
          disabled={readOnly}
        />
      </div>
      <div className={classNames(styles.info, 'items-center')}>
        <span className="typo-text-14--bold text-faded">{t('general.phone')}</span>
        <ControlledInput
          control={control}
          {...register('phone')}
          placeholder={t('registration.placeholders.phone')}
          disabled={readOnly}
        />
      </div>
    </form>
  );
});
